import React, { useMemo, useEffect, useState } from 'react';
import {
  useMaterialReactTable,
  MRT_TableContainer,
} from 'material-react-table';

const LeadershipReviewSummaryTable = ({ formik, reviewData }) => {
  const [tableData, setTableData] = useState([]);

  // Helper Calculation
  const calculateSectionStats = (sectionId) => {
    const sectionData = formik.values.leadership_reviews_and_development_plan?.competencies?.[sectionId];
    if (!sectionData || !sectionData.scores) return { avgSelf: 0, avgSup: 0, gap: 0 };

    let totalSelf = 0;
    let totalSup = 0;
    let countSelf = 0;
    let countSup = 0;

    const sectionDef = reviewData.find((s) => s.id === sectionId);
    if (!sectionDef) return { avgSelf: 0, avgSup: 0, gap: 0 };

    sectionDef.questions.forEach((_, index) => {
      const scores = sectionData.scores[index];
      if (scores) {
        if (scores.self) {
          totalSelf += Number(scores.self);
          countSelf++;
        }
        if (scores.supervisor) {
          totalSup += Number(scores.supervisor);
          countSup++;
        }
      }
    });

    const avgSelf = countSelf > 0 ? (totalSelf / countSelf).toFixed(1) : 0;
    const avgSup = countSup > 0 ? (totalSup / countSup).toFixed(1) : 0;
    const gap = (Number(avgSup) - Number(avgSelf)).toFixed(1);

    return { avgSelf, avgSup, gap };
  };

  // Sync data with formik values and order
  useEffect(() => {
    // 1. Get current order from formik or default to reviewData order
    const savedOrder = formik.values.leadership_reviews_and_development_plan?.summary_order || [];
    
    // 2. Map reviewData to include calculated stats
    const processedData = reviewData.map((section) => {
      const stats = calculateSectionStats(section.id);
      return {
        id: section.id,
        title: section.title,
        ...stats,
      };
    });

    // 3. Sort based on saved order
    let orderedData = [];
    if (savedOrder.length > 0) {
        // Create a map for quick access
        const dataMap = new Map(processedData.map(item => [item.id, item]));
        
        // precise order from saved state
        const reordered = savedOrder.map(id => dataMap.get(id)).filter(item => item !== undefined);
        
        // append any new sections that might not be in saved order yet (schema evolution)
        const remaining = processedData.filter(item => !savedOrder.includes(item.id));
        
        orderedData = [...reordered, ...remaining];
    } else {
        orderedData = processedData;
    }

    setTableData(orderedData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.leadership_reviews_and_development_plan?.competencies, formik.values.leadership_reviews_and_development_plan?.summary_order, reviewData]); // Re-calc when scores change


  const columns = useMemo(
    () => [
      {
        accessorKey: 'priority_index',
        header: 'Priority',
        size: 80,
        Cell: ({ row }) => (
          <span style={{ fontWeight: row.index < 3 ? 'bold' : 'normal', color: row.index < 3 ? '#d32f2f' : 'inherit' }}>
            {row.index + 1}
          </span>
        ),
        muiTableHeadCellProps: {
            style: {
                background: "#FEC025",
                color: "black",
                textAlign: 'center'
            }
        },
        muiTableCellProps: {
            style: {
                textAlign: 'center'
            }
        }
      },
      {
        accessorKey: 'title',
        header: 'Competency',
        size: 300,
        muiTableHeadCellProps: {
            style: {
                background: "#FEC025",
                color: "black",
            }
        }
      },
      {
        accessorKey: 'avgSelf',
        header: 'Avg Self Score',
        size: 100,
        muiTableHeadCellProps: {
            style: {
                background: "#FEC025",
                color: "black",
                textAlign: "center"
            }
        },
        muiTableCellProps: {
            style: {
                textAlign: 'center'
            }
        }
      },
      {
        accessorKey: 'avgSup',
        header: 'Avg Supervisor Score',
        size: 100,
        muiTableHeadCellProps: {
            style: {
                background: "#FEC025",
                color: "black",
                textAlign: "center"
            }
        },
        muiTableCellProps: {
            style: {
                textAlign: 'center'
            }
        }
      },
      {
        accessorKey: 'gap',
        header: 'Gap (+/-)',
        size: 100,
        Cell: ({ cell }) => {
            const val = Number(cell.getValue());
            const color = val < 0 ? 'red' : val > 0 ? 'green' : 'black';
            return <span style={{ color, fontWeight: 'bold' }}>{val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)}</span>;
        },
        muiTableHeadCellProps: {
            style: {
                background: "#FEC025",
                color: "black",
                textAlign: "center"
            }
        },
        muiTableCellProps: {
            style: {
                textAlign: 'center'
            }
        }
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    autoResetPageIndex: false,
    columns,
    data: tableData,
    enableRowOrdering: true,
    enableSorting: false,
    enablePagination: false, 
    enableBottomToolbar: false,
    enableTopToolbar: false,
    muiTableBodyRowProps: ({ row }) => ({
        sx: {
            backgroundColor: row.index < 3 ? '#fff8e1' : 'white', // Highlight top 3
        }
    }),
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = table.getState();
        if (hoveredRow && draggingRow) {
          const newData = [...tableData];
          // move item
          const [movedItem] = newData.splice(draggingRow.index, 1);
          newData.splice(hoveredRow.index, 0, movedItem);
          
          setTableData(newData);
          
          // Update Formik with new order of IDs
          const newOrderIds = newData.map(item => item.id);
          formik.setFieldValue('leadership_reviews_and_development_plan.summary_order', newOrderIds);
        }
      },
    }),
  });

  return (
    <div className="summary-table-container">
        <MRT_TableContainer table={table} />
        <p className="mt-2 text-sm text-gray-500 italic">* Drag and drop rows to set the top 3 priorities for the Development Plan.</p>
    </div>
  );
};

export default LeadershipReviewSummaryTable;
